// Entrada de cliente novo: geração do link e conversão dos dados enviados para o cadastro.

export const INTAKE_BUCKET = 'client-intake';
export const INTAKE_DAYS = 7;

export const INTAKE_KIND_LABELS: Record<string, string> = {
  identidade: 'Documento com foto',
  cpf: 'CPF',
  residencia: 'Comprovante de residência',
  outros: 'Outros documentos',
};

export interface IntakeData {
  nome: string;
  cpf: string;
  rg?: string;
  nascimento: string;
  estado_civil: string;
  profissao: string;
  nacionalidade?: string;
  email: string;
  celular: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
}

export const onlyDigits = (value?: string | null) => String(value || '').replace(/\D/g, '');

export const formatCpf = (cpf: string) => {
  const d = onlyDigits(cpf);
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf;
};

export const formatPhone = (phone: string) => {
  const d = onlyDigits(phone);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
};

export const formatCep = (cep: string) => {
  const d = onlyDigits(cep);
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : cep;
};

export function isValidCpf(value: string) {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const check = (size: number) => {
    let sum = 0;
    for (let i = 0; i < size; i++) sum += Number(cpf[i]) * (size + 1 - i);
    const rest = (sum * 10) % 11;
    return (rest === 10 ? 0 : rest) === Number(cpf[size]);
  };
  return check(9) && check(10);
}

/** Link novo: só o resumo fica salvo, então o endereço completo só existe na hora em que é gerado */
export async function generateIntakeToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const token = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  const hash = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  return { token, hash };
}

export const intakeUrl = (origin: string, token: string) => `${origin}/cadastro/${token}`;

export const intakeInvite = (label: string, url: string) => {
  const first = label.trim().split(/\s+/)[0] || '';
  const name = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  return [
    `Olá${name ? `, ${name}` : ''}! Tudo bem?`,
    '',
    'Para darmos início ao seu atendimento, peço que preencha seus dados e envie seus documentos neste link seguro:',
    url,
    '',
    `Ele fica disponível por ${INTAKE_DAYS} dias e leva poucos minutos. Tenha em mãos um documento com foto e um comprovante de residência.`,
    '',
    'Um abraço,',
    'Allana Sthéfany',
  ].join('\n');
};

/** Dados enviados pela pessoa no formato do cadastro de clientes */
export function intakeToClient(data: IntakeData, userId: string, consentAt: string | null) {
  return {
    user_id: userId,
    client_type: 'person',
    name: data.nome,
    email: data.email,
    mobile: formatPhone(data.celular),
    cpf_cnpj: formatCpf(data.cpf),
    rg_ie: data.rg || null,
    birth_date: data.nascimento,
    nationality: data.nacionalidade || 'Brasileira',
    marital_status: data.estado_civil,
    profession: data.profissao,
    zip_code: formatCep(data.cep),
    address: data.endereco,
    address_number: data.numero,
    complement: data.complemento || null,
    district: data.bairro,
    city: data.cidade,
    state: data.uf,
    country: 'Brasil',
    lgpd_consent: !!consentAt,
    lgpd_consent_date: consentAt,
  };
}

/** "identidade__uuid.pdf" -> "identidade" */
export const intakeFileKind = (name: string) => name.split('__')[0];
